import { useFormatter, type DateTimeFormatOptions } from 'next-intl';
import type { DateFormat } from 'core/i18n/formats';
import { localTimeZone } from 'utils/DateTimeUtils';

/**
 * Formats a date with next-intl, so it's in the same language as the rest
 * of the page, using one of the named formats in core/i18n/formats.ts or
 * explicit options.
 *
 * It uses the browser's time zone: a memo's date, a billing date or a day
 * in a picker is a moment in time, and which day and hour it falls on
 * depends on where the reader is. That's only known in the browser, so use
 * this in components that render on the client. Passing the zone also keeps
 * next-intl from warning that none is configured.
 *
 * `date` is anything `new Date()` accepts: a Date, an ISO string, or
 * milliseconds since 1970.
 */
export const useFormatDate = () => {
  const format = useFormatter();
  const timeZone = localTimeZone();

  return (
    date: Date | string | number,
    style: DateFormat | DateTimeFormatOptions,
  ): string =>
    typeof style === 'string'
      ? format.dateTime(new Date(date), style, { timeZone })
      : format.dateTime(new Date(date), { ...style, timeZone });
};

export type FormatDate = ReturnType<typeof useFormatDate>;
